import api from '@/services/api'

export const TIERS = ['basic', 'basic_plus', 'pro'] as const
export type TierKey = (typeof TIERS)[number]
export const CYCLES = ['monthly', 'quarterly', 'yearly'] as const
export type BillingCycle = (typeof CYCLES)[number]

export const TIER_NAMES: Record<TierKey, string> = { basic: 'Basic', basic_plus: 'Basic+', pro: 'Pro' }
export const CYCLE_LABELS: Record<BillingCycle, string> = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' }
export const CYCLE_PERIOD: Record<BillingCycle, string> = { monthly: 'month', quarterly: 'quarter', yearly: 'year' }

export const TIER_FEATURES: Record<TierKey, string[]> = {
  basic: ['5 generations a month', '1 platform per generation', '1 workspace', '5 saved prompts'],
  basic_plus: ['15 generations a month', '2 platforms per generation', '3 workspaces', '15 AI images a month', 'Service-specific posts'],
  pro: ['50 generations a month', '4 platforms per generation', '8 workspaces', '30 AI images a month', 'Context-aware AI images', 'Post to X (Twitter)'],
}

export function tierRank(tier: string | null | undefined): number {
  return TIERS.indexOf(tier as TierKey)
}

export interface TierPrice {
  monthly: number
  quarterly: number
  yearly: number
  monthly_display: string
  quarterly_display: string
  yearly_display: string
}
export type Pricing = { currency: string; currency_symbol: string } & Record<TierKey, TierPrice>

/** The INR prices actually charged (paise + display), from GET /payments/pricing. */
export function loadPricing(): Promise<Pricing> {
  return api.getPaymentPricing()
}

export function priceDisplay(pricing: Pricing, tier: TierKey, cycle: BillingCycle): string {
  return pricing[tier][`${cycle}_display`]
}

// Display-only approximations; Razorpay always charges the INR price above.
export const APPROX_CURRENCIES = ['USD', 'AED', 'EUR'] as const
export type ApproxCurrency = (typeof APPROX_CURRENCIES)[number]
const APPROX_SYMBOL: Record<ApproxCurrency, string> = { USD: '$', AED: 'AED ', EUR: '€' }
const APPROX_PRICES: Record<ApproxCurrency, Record<TierKey, Record<BillingCycle, number>>> = {
  USD: { basic: { monthly: 10, quarterly: 27, yearly: 102 }, basic_plus: { monthly: 25, quarterly: 67, yearly: 255 }, pro: { monthly: 50, quarterly: 135, yearly: 510 } },
  AED: { basic: { monthly: 40, quarterly: 108, yearly: 408 }, basic_plus: { monthly: 100, quarterly: 270, yearly: 1020 }, pro: { monthly: 200, quarterly: 540, yearly: 2040 } },
  EUR: { basic: { monthly: 10, quarterly: 27, yearly: 102 }, basic_plus: { monthly: 25, quarterly: 67, yearly: 255 }, pro: { monthly: 50, quarterly: 135, yearly: 510 } },
}

export function approxDisplay(currency: ApproxCurrency, tier: TierKey, cycle: BillingCycle): string {
  return `≈ ${APPROX_SYMBOL[currency]}${APPROX_PRICES[currency][tier][cycle].toLocaleString()} (charged in INR)`
}

/** The API marks "no cap" with a negative limit or the 999999 sentinel; neither is ever shown as a number. */
export function isUnlimited(limit: number | null | undefined): boolean {
  return typeof limit === 'number' && (limit < 0 || limit >= 999999)
}

export function limitText(limit: number | null | undefined): string {
  if (limit === null || limit === undefined) return 'Not known yet'
  return isUnlimited(limit) ? 'Unlimited' : String(limit)
}

export function formatPaise(amount: number | null | undefined, currency = 'INR'): string {
  if (amount === null || amount === undefined) return 'Not known yet'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount / 100)
}
